import { cloudFrontHeadersSchema } from "@app/schemas/cfSchemas";
import { UserStatus, UserType } from "@app/schemas/mySphUser";
import { HandlerParams } from "@sphtech/web2-core/middleware";

export const FlagNames = {
  Trial: "trial",
  RegBt: "reg_bt",
  BtPremium: "bt_premium",
  BtMoePoly: "bt_moe_poly",
  BtMoeJC: "bt_moe_jc",
  BtMoeITE: "bt_moe_ite",
  BhGenG: "bh_geng",
};

type ServiceTypeKey = keyof typeof ServiceTypeMap;
type ServiceTypeValue = (typeof ServiceTypeMap)[ServiceTypeKey];
export const ServiceTypeMap = {
  Anonymous: 1,
  PremiumUser: 2,
  RegisteredUser: 3,
  CorpLanUser: 4,
  LiteSubscriber: 5,
  FreeTrial: 6,
  EmailVerifiedRegisteredUser: 12,
  EmailUnverifiedRegisteredUser: 13,
  MoeJCUser: 8,
  MoePolyUser: 9,
  MoeITEUser: 10,
  MHAUser: 14,
  BhGenGUser: 15,
};

type AnalyticsInput = {
  userType?: UserType;
  serviceFlags?: string[];
  userStatus?: UserStatus;
  loginId?: string;
};

export function getVisitorCat({
  userType = UserType.Anonymous,
  serviceFlags = [],
  userStatus = UserStatus.None,
  loginId = "",
}: AnalyticsInput): ServiceTypeValue {
  if (userType === UserType.Subscriber) {
    return loginId ? ServiceTypeMap.PremiumUser : ServiceTypeMap.CorpLanUser;
  }

  if (userType === UserType.Registered) {
    return userStatus === UserStatus.Verified
      ? ServiceTypeMap.EmailVerifiedRegisteredUser
      : ServiceTypeMap.EmailUnverifiedRegisteredUser;
  }

  for (const svc of serviceFlags) {
    switch (svc) {
      case FlagNames.BhGenG:
        return ServiceTypeMap.BhGenGUser;
      case FlagNames.BtMoePoly:
        return ServiceTypeMap.MoePolyUser;
      case FlagNames.BtMoeITE:
        return ServiceTypeMap.MoeITEUser;
      case FlagNames.BtMoeJC:
        return ServiceTypeMap.MoeJCUser;
      case FlagNames.Trial:
        return ServiceTypeMap.FreeTrial;
    }
  }

  return ServiceTypeMap.Anonymous;
}

export function getUserType(loginId: string, serviceFlags: string[]): UserType {
  let authType = UserType.Anonymous;
  // define what is the service flag you want to check against
  const svcFlagsSubscriber = ["bh_online"];

  if (loginId !== "") {
    authType = UserType.Registered;

    if (serviceFlags.length > 0) {
      svcFlagsSubscriber.forEach((flag) => {
        if (
          serviceFlags.map((v) => v.toLowerCase()).includes(flag.toLowerCase())
        ) {
          authType = UserType.Subscriber;
        }
      });
    }
  }

  // https://github.com/SPHTech/nodejs-proxy/blob/1b7ff8fbf0c6280555f062df16dba2da9150c66a/app/src/middlewares/oktaAuth/authHeadersHandler.ts#L50-L61
  // add logic to check if the request is internal or fb crawler request
  // if internal req or fb crawler req, then set authType to y-sub
  // if not internal req or fb crawler req, then set authType to y-reg

  return authType;
}

/**
 * Retrieves the value of a cookie from the CloudFront request headers.
 *
 * Returns `undefined` when the headers do not match the expected structure
 * or when the requested cookie is not present.
 *
 * @param context CloudFront handler context containing the request headers.
 * @param cookieName Name of the cookie to retrieve.
 * @returns The cookie value, or `undefined` when it cannot be found.
 */
export function getValueFromCookie(context: HandlerParams, cookieName: string) {
  const result = cloudFrontHeadersSchema.safeParse(
    context.event.Records[0].cf.request.headers,
  );
  const headers = result.success ? result.data : undefined;

  const cookieHeader = headers?.["cookie"]?.[0]?.value ?? "";

  return cookieHeader
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${cookieName}=`))
    ?.split("=")[1];
}
